import { Action, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";
import { ExerciseService } from "../services/exercise.service";
import { ExerciseActions } from "./exercise.actions";
import { Exercise } from "../models/exercise.model";
import { CurrentExercise } from '../models/current-exercise.model';

export class ExerciseStateModel {
    public exercises: Exercise[];
    public currentExercise: CurrentExercise;
    public selectedExercise: Exercise;
    public isLoadingExercises: boolean;
    public error: string;
  }

  const defaults = {
    exercises: [],
    currentExercise: null,
    selectedExercise: null,
    isLoadingExercises: false,
    error: null
  };

  @State<ExerciseStateModel>({
    name: 'exercise',
    defaults,
  })
  @Injectable()
  export class ExerciseState {
    constructor(
      private alertService: AlertService,
      private localizationService: LocalizationService,
      private exerciseService: ExerciseService,
      public store: Store,
    ) {}

  @Action(ExerciseActions.GetExercises)
  getExercises (ctx: StateContext<ExerciseStateModel>, { }: ExerciseActions.GetExercises) {
    return this.exerciseService.getExercises()
      .subscribe({
        next: (response) =>
          ctx.dispatch(new ExerciseActions.GetExercisesSuccess(response)),
        error: (error) =>
          ctx.dispatch(new ExerciseActions.Error(error)),
      });
  }

  @Action(ExerciseActions.GetExercisesSuccess)
  getContractSuccess(ctx: StateContext<ExerciseStateModel>, { exercises }: ExerciseActions.GetExercisesSuccess) {
    ctx.patchState({
      exercises: exercises.sort((a, b) => b.exerciseId - a.exerciseId),
      isLoadingExercises: false,
    });
  }

  @Action(ExerciseActions.GetCurrentExercise)
  getCurrentExercise (ctx: StateContext<ExerciseStateModel>, { }: ExerciseActions.GetCurrentExercise) {
    return this.exerciseService.getCurrentExercise()
      .subscribe({
        next: (response) =>
          ctx.dispatch(new ExerciseActions.GetCurrentExerciseSuccess(response)),
        error: (error) =>
          ctx.dispatch(new ExerciseActions.Error(error)),
      });
  }

  @Action(ExerciseActions.GetCurrentExerciseSuccess)
  getCurrentExerciseSuccess(ctx: StateContext<ExerciseStateModel>, { exercise }: ExerciseActions.GetCurrentExerciseSuccess) {
    ctx.patchState({
      currentExercise: exercise,
      isLoadingExercises: false,
    });
  }

  @Action(ExerciseActions.CreateExercise)
  createExercise (ctx: StateContext<ExerciseStateModel>, { startYear, endYear }: ExerciseActions.CreateExercise) {
    this.alertService.confirm(
      this.localizationService.translate("exercises.createExercise"),
      this.localizationService.translate('exercises.createExerciseConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('exercises.createExerciseConfirm')
      .replace('{0}', startYear.toString())
      .replace('{1}', endYear.toString())
      ).then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingExercises: true });
          return this.exerciseService.createExercise(startYear)
          .subscribe({
            next: () =>
              ctx.dispatch(new ExerciseActions.CreateExerciseSuccess()),
            error: (error) =>
              ctx.dispatch(new ExerciseActions.Error(error)),
          });
        } else {
          return null;
        }
      });
    }

  @Action(ExerciseActions.CreateExerciseSuccess)
  createExerciseSuccess(ctx: StateContext<ExerciseStateModel>, { }: ExerciseActions.CreateExerciseSuccess) {
    ctx.patchState({
      isLoadingExercises: false,
    });
  }

  @Action(ExerciseActions.SetSelectedExercise)
  setSelectedExercise(ctx: StateContext<ExerciseStateModel>, { exerciseId }: ExerciseActions.SetSelectedExercise) {
    const selectedExercise = ctx.getState().exercises.find((exercise) => exercise.exerciseId === Number(exerciseId));
    ctx.patchState({
      selectedExercise,
    });
  }

  @Action(ExerciseActions.Error)
  onErrorOcurred(ctx: StateContext<ExerciseStateModel>, {error}: ExerciseActions.Error) {
    ctx.patchState({
      error: error,
      isLoadingExercises: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(ExerciseActions.SetExerciseDefaultState)
  setHolidaysDefaultState(ctx: StateContext<ExerciseStateModel>, {}: ExerciseActions.SetExerciseDefaultState
  ) {
    ctx.setState(
      defaults
    );
  }

  private getTitle() {
    return this.localizationService.translate('labels.exercise');
  }
}
